import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, LogOut, Search, Users, X, FileText, StickyNote, Phone, Mail, Save,
} from "lucide-react";
import { API } from "@/lib/api";

const STATUSES = ["New", "Contacted", "Quoted", "Application Started", "Submitted", "Issued", "Declined", "Closed"];

const api = axios.create({ baseURL: API, withCredentials: true });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [quotes, setQuotes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authChecked) return;
    api.get("/auth/me")
      .then((res) => { setUser(res.data); setAuthChecked(true); })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [authChecked, navigate]);

  const load = () => {
    setLoading(true);
    api.get("/admin/quotes", { params: { status: statusFilter, search } })
      .then((res) => setQuotes(res.data))
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 403) navigate("/admin/login", { replace: true });
        else toast.error("Could not load quote requests.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!authChecked) return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, statusFilter, search]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  const counts = useMemo(() => {
    const c = { All: quotes.length };
    return c;
  }, [quotes]);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="admin-loading">
        <p className="font-serif text-xl text-[#0B2319]">Loading portal...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard">
      <header className="bg-[#0B2319] border-b border-[#D4AF37]/30 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full border border-[#D4AF37]/60 flex items-center justify-center">
              <span className="font-serif font-bold text-[#D4AF37] text-xs tracking-widest">SC</span>
            </div>
            <div>
              <p className="font-serif font-semibold text-[#FDFBF7] text-sm leading-tight">Lead Management Portal</p>
              <p className="text-[10px] text-[#C5A059] tracking-[0.2em] uppercase">Simonne &amp; Co. Financial Solutions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-[#9DB0A6]" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} data-testid="logout-button"
                    className="flex items-center gap-1.5 text-xs text-[#D8CDB8] hover:text-[#FCF6BA] transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="status-filters">
            {["All", ...STATUSES].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`whitespace-nowrap px-4 py-2 rounded-full border text-xs font-semibold transition-colors ${
                        statusFilter === s
                          ? "bg-[#0B2319] text-[#FCF6BA] border-[#D4AF37]"
                          : "bg-[#FAF7F0] text-[#1A231F] border-[#D4AF37]/30 hover:border-[#D4AF37]"
                      }`}>
                {s}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5A6B63]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load()}
                   data-testid="search-input" placeholder="Search client name..."
                   className="w-full sm:w-64 h-11 pl-10 pr-10 rounded-xl border border-[#D4AF37]/30 bg-white text-sm focus:outline-none focus:border-[#C5A059]" />
            {search && (
              <button onClick={() => { setSearch(""); setTimeout(load, 0); }} data-testid="clear-search-button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A6B63]">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="mt-6 space-y-3">
          {loading && <p className="text-sm text-[#5A6B63]" data-testid="quotes-loading">Loading requests...</p>}
          {!loading && quotes.length === 0 && (
            <div className="card-gold p-10 text-center" data-testid="quotes-empty">
              <FileText size={28} className="mx-auto text-[#C5A059]" />
              <p className="font-serif text-lg text-[#0B2319] mt-3">No quote requests found</p>
              <p className="text-sm text-[#5A6B63] mt-1">New submissions will appear here.</p>
            </div>
          )}
          {!loading && quotes.map((q) => (
            <button key={q.request_id} onClick={() => setSelected(q)} data-testid={`quote-row-${q.request_id}`}
                    className="card-gold w-full p-4 sm:p-5 text-left hover:shadow-lg transition-shadow">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
                <div>
                  <p className="font-serif font-semibold text-[#0B2319] text-lg">{q.contact.full_name}</p>
                  <p className="text-xs text-[#5A6B63] mt-0.5">
                    {new Date(q.created_at).toLocaleDateString()} &middot; {q.contact.phone} &middot; {q.coverage_for}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {q.insureds.length > 1 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#FAF4E8] border border-[#D4AF37]/40 text-[#0B2319]">
                      <Users size={11} /> {q.insureds.length} people
                    </span>
                  )}
                  <StatusBadge status={q.status} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <QuoteDetail quote={selected} onClose={() => setSelected(null)}
                     onUpdated={(u) => { setSelected(u); load(); }} />
      )}
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const colors = {
    New: "bg-[#0B2319] text-[#FCF6BA]",
    Contacted: "bg-[#FAF4E8] text-[#0B2319] border border-[#D4AF37]/50",
    Quoted: "bg-[#C5A059]/20 text-[#0B2319] border border-[#C5A059]/60",
    Issued: "bg-emerald-900 text-emerald-50",
    Declined: "bg-red-900/90 text-red-50",
    Closed: "bg-[#5A6B63] text-white",
  };
  return (
    <span data-testid={`status-badge-${status.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`text-[10px] font-bold px-3 py-1.5 rounded-full ${colors[status] || "bg-[#EFE9DC] text-[#0B2319]"}`}>
      {status}
    </span>
  );
};

function QuoteDetail({ quote, onClose, onUpdated }) {
  const [form, setForm] = useState({
    status: quote.status, carrier_quoted: quote.carrier_quoted || "",
    face_amount: quote.face_amount || "", quoted_premium: quote.quoted_premium || "",
    follow_up_date: quote.follow_up_date || "",
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/quotes/${quote.request_id}`, form);
      toast.success("Saved.");
      onUpdated(res.data);
    } catch {
      toast.error("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const res = await api.post(`/admin/quotes/${quote.request_id}/notes`, { text: note.trim() });
      setNote("");
      onUpdated(res.data);
    } catch {
      toast.error("Could not add note.");
    }
  };

  const Row = ({ label, value }) => value ? (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-[#5A6B63]">{label}</span>
      <span className="text-[#1A231F] font-medium text-right">{value}</span>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="quote-detail-drawer">
      <div className="absolute inset-0 bg-[#081A13]/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#FDFBF7] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#0B2319] px-5 py-4 flex items-center justify-between z-10">
          <div>
            <p className="font-serif font-semibold text-[#FDFBF7] text-lg">{quote.contact.full_name}</p>
            <p className="text-[10px] text-[#C5A059] tracking-widest uppercase">Quote request &middot; {new Date(quote.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} data-testid="close-detail-button" className="text-[#D8CDB8] hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Contact */}
          <section className="card-gold p-4" data-testid="detail-contact-section">
            <p className="text-xs font-bold tracking-widest uppercase text-[#B8860B] mb-2">Contact</p>
            <Row label="Phone" value={quote.contact.phone} />
            <Row label="Email" value={quote.contact.email} />
            <Row label="Preferred" value={`${quote.contact.preferred_contact || "—"} ${quote.contact.best_time ? `(${quote.contact.best_time})` : ""}`} />
            <Row label="Coverage for" value={quote.coverage_for} />
            <Row label="Coverage amount" value={quote.coverage_amount} />
            <Row label="Types of interest" value={quote.coverage_types.join(", ")} />
          </section>

          {/* Insureds */}
          <section className="card-gold p-4" data-testid="detail-insureds-section">
            <p className="text-xs font-bold tracking-widest uppercase text-[#B8860B] mb-2">
              Proposed insured ({quote.insureds.length})
            </p>
            {quote.insureds.map((p, i) => (
              <div key={i} className="border-t border-[#D4AF37]/20 first:border-t-0 pt-3 first:pt-0 mt-3 first:mt-0"
                   data-testid={`detail-insured-${i}`}>
                <p className="font-serif font-semibold text-[#0B2319]">{p.full_name}</p>
                <Row label="DOB / Gender" value={`${p.dob || "—"} / ${p.gender || "—"}`} />
                <Row label="State" value={p.state} />
                <Row label="Relationship" value={p.relationship} />
                <Row label="Smoker" value={p.smoker} />
                <Row label="Desired amount" value={p.coverage_amount} />
                <Row label="Reason" value={p.reason} />
              </div>
            ))}
          </section>

          {/* Financial + Health + Current coverage */}
          <section className="card-gold p-4" data-testid="detail-financial-section">
            <p className="text-xs font-bold tracking-widest uppercase text-[#B8860B] mb-2">Financial needs</p>
            <Row label="Monthly income to replace" value={quote.financial.monthly_income} />
            <Row label="Mortgage" value={quote.financial.has_mortgage} />
            <Row label="Mortgage balance" value={quote.financial.mortgage_balance} />
            <Row label="Monthly payment" value={quote.financial.mortgage_payment} />
            <Row label="Dependents" value={quote.financial.has_dependents === "Yes" ? `Yes (${quote.financial.dependents_count || "?"})` : quote.financial.has_dependents} />
            <Row label="Help with" value={quote.financial.help_with.join(", ")} />
          </section>

          <section className="card-gold p-4" data-testid="detail-health-section">
            <p className="text-xs font-bold tracking-widest uppercase text-[#B8860B] mb-2">Health snapshot</p>
            <Row label="Height / Weight" value={`${quote.health.height || "—"} / ${quote.health.weight || "—"}`} />
            <Row label="Tobacco" value={quote.health.tobacco} />
            <Row label="Tobacco details" value={quote.health.tobacco_details} />
            <Row label="Conditions" value={quote.health.conditions.join(", ")} />
            <Row label="Notes" value={quote.health.condition_notes} />
            <Row label="Medications" value={quote.health.medications} />
            <Row label="Medication names" value={quote.health.medication_names} />
          </section>

          <section className="card-gold p-4" data-testid="detail-current-coverage-section">
            <p className="text-xs font-bold tracking-widest uppercase text-[#B8860B] mb-2">Current coverage</p>
            <Row label="Has life insurance" value={quote.current_coverage.has_life_insurance} />
            <Row label="Existing amount" value={quote.current_coverage.existing_amount} />
            <Row label="Existing type" value={quote.current_coverage.existing_type} />
            <Row label="Ownership" value={quote.current_coverage.existing_ownership} />
            <Row label="Looking to" value={quote.current_coverage.looking_to} />
          </section>

          {/* Admin tracking */}
          <section className="card-gold p-4" data-testid="detail-tracking-section">
            <p className="text-xs font-bold tracking-widest uppercase text-[#B8860B] mb-3">Policy tracking</p>
            <label className="block mb-3">
              <span className="block text-xs font-semibold text-[#0B2319] mb-1">Status</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid="status-select"
                      className="w-full h-11 px-3 rounded-xl border border-[#D4AF37]/30 bg-white text-sm">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <span className="block text-xs font-semibold text-[#0B2319] mb-1">Carrier quoted</span>
                <input value={form.carrier_quoted} onChange={(e) => setForm({ ...form, carrier_quoted: e.target.value })}
                       data-testid="carrier-quoted-input" placeholder="e.g. Mutual of Omaha"
                       className="w-full h-11 px-3 rounded-xl border border-[#D4AF37]/30 bg-white text-sm" />
              </label>
              <label>
                <span className="block text-xs font-semibold text-[#0B2319] mb-1">Face amount</span>
                <input value={form.face_amount} onChange={(e) => setForm({ ...form, face_amount: e.target.value })}
                       data-testid="face-amount-input" placeholder="$250,000"
                       className="w-full h-11 px-3 rounded-xl border border-[#D4AF37]/30 bg-white text-sm" />
              </label>
              <label>
                <span className="block text-xs font-semibold text-[#0B2319] mb-1">Quoted premium</span>
                <input value={form.quoted_premium} onChange={(e) => setForm({ ...form, quoted_premium: e.target.value })}
                       data-testid="quoted-premium-input" placeholder="$48/mo"
                       className="w-full h-11 px-3 rounded-xl border border-[#D4AF37]/30 bg-white text-sm" />
              </label>
              <label>
                <span className="block text-xs font-semibold text-[#0B2319] mb-1 flex items-center gap-1">
                  <Calendar size={11} /> Follow-up date
                </span>
                <input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                       data-testid="follow-up-date-input"
                       className="w-full h-11 px-3 rounded-xl border border-[#D4AF37]/30 bg-white text-sm" />
              </label>
            </div>
            <button onClick={save} disabled={saving} data-testid="save-tracking-button"
                    className="mt-4 w-full h-11 rounded-xl bg-[#0B2319] text-[#FCF6BA] text-sm font-semibold border border-[#D4AF37]/40 inline-flex items-center justify-center gap-2 hover:bg-[#081A13] transition-colors disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving..." : "Save changes"}
            </button>
          </section>

          {/* Notes */}
          <section className="card-gold p-4" data-testid="detail-notes-section">
            <p className="text-xs font-bold tracking-widest uppercase text-[#B8860B] mb-3 flex items-center gap-1.5">
              <StickyNote size={13} /> Internal notes
            </p>
            <div className="space-y-2 mb-3">
              {(quote.notes || []).length === 0 && (
                <p className="text-xs text-[#5A6B63]" data-testid="notes-empty">No notes yet.</p>
              )}
              {(quote.notes || []).map((n, i) => (
                <div key={i} className="bg-[#FAF4E8] border border-[#D4AF37]/30 rounded-lg px-3 py-2" data-testid={`note-${i}`}>
                  <p className="text-sm text-[#1A231F]">{n.text}</p>
                  <p className="text-[10px] text-[#5A6B63] mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input"
                     placeholder="Add a note..."
                     className="flex-1 h-11 px-3 rounded-xl border border-[#D4AF37]/30 bg-white text-sm" />
              <button onClick={addNote} data-testid="add-note-button"
                      className="h-11 px-4 rounded-xl border border-[#D4AF37] text-[#0B2319] text-sm font-semibold hover:bg-[#FAF4E8] transition-colors">
                Add
              </button>
            </div>
          </section>

          <div className="flex gap-4 justify-center text-xs text-[#5A6B63] pb-6">
            <a href={`tel:${quote.contact.phone}`} data-testid="detail-call-link" className="flex items-center gap-1">
              <Phone size={12} className="text-[#C5A059]" /> Call
            </a>
            <a href={`mailto:${quote.contact.email}`} data-testid="detail-email-link" className="flex items-center gap-1">
              <Mail size={12} className="text-[#C5A059]" /> Email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
